import './AbroadCard.css';
import { useId, useLayoutEffect, useRef, useState } from 'preact/hooks';
import { Button } from '../../components/Button';
import { CandidateRow } from '../../components/CandidateRow';
import { Pager } from '../../components/Pager';
import { SectionLabel } from '../../components/SectionLabel';
import { UfTile } from '../../components/UfTile';
import { t } from '../../copy/strings';
import { fmtPct, fmtQtyShort, fmtSections, fmtVoters } from '../../format/numbers';
import { placeName } from '../../format/places';
import { fmtHourShort } from '../../format/time';
import { abroadClosing, cityRows, countryRows, stillVoting } from '../../model/abroad';
import { ref2022Of } from '../../model/ref2022';
import type { LayoutMode } from '../../model/types';
import { openProfile } from '../../state/actions';
import {
  useConfig,
  useHeadline,
  useLayout,
  useNowMinute,
  usePhase,
  useStatics,
  useTimeline,
  useView,
} from '../../state/hooks';
import { partyClass, partyOf } from '../../theme/parties';
import { CountryRow } from './CountryRow';
import { PlaceCard } from './PlaceCard';
import { PlaceHeader } from './PlaceHeader';
import { UfNavButtons } from './UfNavButtons';
import { useStateDetail } from './useStateDetail';

const PHONE_MODES: ReadonlySet<LayoutMode> = new Set(['flow', 'tablet', 'land']);
/** Linha de país (§8.5): 50 px. */
const ROW_PX = 50;
/** O que vem depois da lista no cartão: o botão "Ver todos" e a nota de rodapé (px lógicos, com os espaços). */
const BELOW_LIST_PX = 96;
/** Celular: 8 países no modo resumido e 20 por página no completo (§8.5 bloco 6). */
const PHONE_ROWS = 8;
const PHONE_PAGE = 20;

const pct1 = (v: number): string => fmtPct(v, 1);

/** "a, b e c" (§8.5: "…, Chile (até 18h) e mais 6 países"). */
function listPt(items: readonly string[]): string {
  if (items.length <= 1) return items.join('');
  return `${items.slice(0, -1).join(', ')}${t('list.and')}${items[items.length - 1]}`;
}

/**
 * Cartão do Exterior (§8.5): o presidente fora do país (antes dos votos, o andamento do fechamento das urnas pelos fusos;
 * depois, as duas linhas e a linha "2022"), e os países em ordem de eleitorado, com "Ver todos os {N} países" e páginas
 * (M14: os 134 alcançáveis só pelo cartão, AC-8.5-01). Lê a vista (`places.ZZ`), a geografia e o detalhe de `ZZ`
 * (`useStateDetail`); os países somam as cidades no cliente (`model/abroad.ts`). Neutralidade: linhas na ordem 13 | 22.
 */
export function AbroadCard() {
  const view = useView();
  const statics = useStatics();
  const config = useConfig();
  const layout = useLayout();
  const phase = usePhase();
  const headline = useHeadline();
  const { live, displayTMin } = useTimeline();
  // o instante do fechamento das urnas: ao vivo, o relógio da fonte (antes do 1º número a linha do tempo não anda);
  // revendo a noite, o instante exibido (§8.5 "Dados")
  const nowTMin = useNowMinute();
  const instant = live ? nowTMin : displayTMin;
  const id = useId();
  const tv = layout.mode === 'tv';
  const phone = PHONE_MODES.has(layout.mode);
  const zoom = layout.zoom || 1;
  const seq = view?.seq ?? null;
  const detail = useStateDetail('ZZ', live, seq);
  const [full, setFull] = useState(false);
  const [page, setPage] = useState(1);
  const [fit, setFit] = useState(PHONE_ROWS);
  const listRef = useRef<HTMLDivElement>(null);
  /** Trocou de página pelo Pager: o foco vai para a 1ª linha da página nova (§8.5 bloco 7). */
  const focusFirst = useRef(false);
  useLayoutEffect(() => {
    if (!focusFirst.current) return;
    focusFirst.current = false;
    listRef.current?.querySelector<HTMLElement>('[data-testid="country-row"]')?.focus();
  }, [page]);

  // desktop: max(3, floor(altura disponível / 50)) (§8.5 "Linhas visíveis"): o espaço do topo da lista ao fim do
  // cartão, menos o botão e a nota, medido de novo quando o cartão muda de tamanho
  useLayoutEffect(() => {
    const el = listRef.current;
    const card = el?.closest<HTMLElement>('.place-card');
    if (!el || !card || phone || typeof ResizeObserver === 'undefined') return;
    const measure = () => {
      // o zoom do palco (§4.2.2) fica no AppShell, não no cartão: os retângulos vêm em px com zoom
      const avail = (card.getBoundingClientRect().bottom - el.getBoundingClientRect().top) / zoom - BELOW_LIST_PX;
      setFit(Math.max(3, Math.floor(avail / ROW_PX)));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(card);
    // o que fica acima da lista muda de altura (o texto do fechamento vira as linhas do par às 17h): medir de novo
    const above = el.parentElement?.previousElementSibling;
    if (above) ro.observe(above);
    return () => ro.disconnect();
  }, [phone, zoom, tv]);

  const geo = statics.geography;
  const place = view?.places.ZZ ?? null;
  const pair = view?.candidates ?? statics.candidates?.president ?? null;
  const photos = config.features.photos;
  const beforeOpen = phase === 'pre_open' || headline === 'countdown';
  const cities = geo ? cityRows(geo.abroad_cities, detail?.value, view?.abroad, pair, instant) : [];
  const countries = geo ? countryRows(geo.countries, cities) : [];
  const closing = abroadClosing(cities);
  const voting = stillVoting(countries);
  const electorate = geo?.places.find((p) => p.code === 'ZZ')?.electorate_ref ?? null;
  const fraction = place?.sectionsFraction ?? 0;
  const hasVotes = !beforeOpen && place !== null && place.validVotes > 0;
  const ref = hasVotes && pair ? ref2022Of(statics.baseline, statics.baseline?.places.ZZ, pair, place.shares) : null;

  const perPage = phone ? (full ? PHONE_PAGE : PHONE_ROWS) : fit;
  const pages = Math.max(1, Math.ceil(countries.length / perPage));
  const current = Math.min(page, pages);
  const shown = full ? countries.slice((current - 1) * perPage, current * perPage) : countries.slice(0, perPage);
  const moreThanFits = countries.length > perPage;

  const stillVotingText =
    voting.items.length === 0
      ? null
      : t('abroad.stillVoting', {
          LISTA: listPt([
            ...voting.items.map((c) => t('abroad.stillVoting.item', { PAIS: c.name, h: fmtHourShort(c.closesTMin) })),
            ...(voting.more > 0 ? [t('abroad.stillVoting.more', { N: voting.more })] : []),
          ]),
        });

  return (
    <PlaceCard kind="abroad" class="abroad-card" state={hasVotes ? 'votes' : beforeOpen ? 'before_open' : 'no_votes'}>
      <PlaceHeader
        title={placeName('ZZ')}
        sub={electorate === null ? null : t('place.sub', { S: fmtSections(fraction), E: fmtVoters(electorate) })}
        seal={<UfTile code="ZZ" fill="plain" size="big" />}
        buttons={tv ? 0 : 1}
      />
      <UfNavButtons kind="abroad" />
      <section class="place-card__section" data-testid="abroad-card-president" aria-labelledby={`${id}-president`}>
        <SectionLabel
          id={`${id}-president`}
          label={t('abroad.section')}
          status={
            place
              ? {
                  status: beforeOpen ? 'no_data' : place.status,
                  sectionsFraction: fraction,
                  decision: null,
                  gender: null,
                }
              : undefined
          }
        />
        {hasVotes && pair ? (
          <>
            <div class="abroad-card__rows">
              {pair.map((c, i) => (
                <CandidateRow
                  key={c.number}
                  candidate={c}
                  photo={photos ? c.photo : null}
                  share={place.shares[i as 0 | 1]}
                  portraitSize={30}
                  onOpen={tv ? undefined : () => openProfile(c.number, 'president', 'list')}
                />
              ))}
            </div>
            {ref && !tv ? (
              <p class="place-ref" data-testid="abroad-card-2022" title={t('compare.2022.title')}>
                <span class="place-ref__label">{t('compare.2022.label')}</span>
                {ref.items.map((it, i) => (
                  <span key={it.number} class={`place-ref__item ${partyClass(partyOf(it.party_code))}`}>
                    {i > 0 ? <span class="sr-only">, </span> : null}
                    <span class="place-ref__swatch" data-dataviz="" aria-hidden="true" />
                    {t('compare.2022.item', { NOME2022: it.name, P: pct1(it.share) })}
                  </span>
                ))}
              </p>
            ) : null}
          </>
        ) : (
          <div class="abroad-card__closing" data-testid="abroad-card-closing">
            <p>
              <span class="abroad-card__closed">
                {t('abroad.closedCities', {
                  N: closing.closed,
                  M: closing.total,
                  Q1: fmtQtyShort(closing.closedElectorate),
                  Q2: fmtQtyShort(closing.electorate),
                })}
              </span>{' '}
              {t(beforeOpen ? 'abroad.beforeOpen' : 'abroad.afterOpen')}
            </p>
            {stillVotingText ? <p data-testid="abroad-card-still-voting">{stillVotingText}</p> : null}
          </div>
        )}
      </section>
      {tv ? null : (
        <section class="place-card__section abroad-card__countries" aria-labelledby={`${id}-countries`}>
          <SectionLabel
            id={`${id}-countries`}
            label={t('abroad.byCountry')}
            actions={
              full && pages > 1 ? (
                <Pager
                  page={current}
                  total={pages}
                  onChange={(n) => {
                    setPage(n);
                    focusFirst.current = true;
                  }}
                />
              ) : (
                <span class="abroad-card__aside">{t('abroad.byCountry.aside', { N: countries.length })}</span>
              )
            }
          />
          <div ref={listRef} class="abroad-card__list" data-testid="abroad-card-countries">
            {shown.map((row) => (
              <CountryRow key={row.country.iso2} row={row} pair={pair} photos={photos} />
            ))}
          </div>
          {moreThanFits || full ? (
            <Button
              class="abroad-card__see-all"
              variant="ghost"
              label={full ? t('abroad.seeLess') : t('abroad.seeAll', { N: countries.length })}
              onClick={() => {
                setFull(!full);
                setPage(1);
              }}
            />
          ) : null}
          <p class="abroad-card__note">{t('abroad.note')}</p>
        </section>
      )}
    </PlaceCard>
  );
}
